import { useParams, generatePath } from 'react-router-dom'
const routeToFilterHasParams = (item, param) => item?.includes(`:${param}`)

const trimSlashes = (value) => value.replace(/^\/+|\/+$/g, '')

// Whole path segments only: as a plain substring, the location calendar's 'schedule' also matched
// the brand screen at 'control-hub/scheduler' and put a stray first crumb on it.
const pathnameHasSegments = (pathname, resourcePath) =>
  Boolean(pathname && resourcePath) &&
  `/${trimSlashes(pathname)}/`.includes(`/${trimSlashes(resourcePath)}/`)

// Site-level routes that need /s/:siteId prefix
const SITE_LEVEL_ROUTES = [
  'schedule',
  'covers',
  'clients',
  'reports',
  'retail',
  'payments',
  'team-access',
  'mobile-setup',
  'settings/location-settings',
  'settings/scheduler-settings',
  'settings/retail-settings',
  'settings/account-settings',
]

// Corporate-level routes that need /c prefix
const CORPORATE_LEVEL_ROUTES = ['user-access', 'control-center', 'control-hub', 'exports']

export const useBreadcrumbs = (routesToFilter, location, dynamicBreadCrumbObject) => {
  const params = useParams()
  const { siteId } = params

  const filterResults = routesToFilter.filter((item) => {
    if (!item || !item.path) {
      return false
    }

    if (location.pathname === '/' || location.pathname === '') {
      return item.path === '/' || item.path === ''
    }

    const splittedPathByParams = item.path.split(':')
    const routeParamKey = splittedPathByParams?.[splittedPathByParams?.length - 1]
    let indexOfParam = -1
    if (dynamicBreadCrumbObject) {
      indexOfParam = Object.keys(dynamicBreadCrumbObject).indexOf(routeParamKey)
    }

    return (
      // Verifies if route (from all routes list) has the last param
      // and if that param is included in the dynamicBreadCrumbObject
      // or if the route string (like 'brands') is included in pathname
      (routeToFilterHasParams(item?.path, routeParamKey) && indexOfParam > -1) ||
      pathnameHasSegments(location?.pathname, item?.resourcePath)
    )
  })

  const breadcrumbs = filterResults.map((item) => {
    const splittedPathByParams = item.path.split(':')

    const routeParamKey = splittedPathByParams?.[splittedPathByParams?.length - 1]

    const hasParamsInRoute = routeToFilterHasParams(item?.path, routeParamKey)
    const hasRequiredParams = Object.keys(params)?.length > 0
    const hasSpecificRequiredParam = routeParamKey && params[routeParamKey]

    let basePath = ''
    if (hasParamsInRoute && hasRequiredParams && routeParamKey && hasSpecificRequiredParam) {
      basePath = item?.path.includes(`:${routeParamKey}`)
        ? generatePath(item?.path, params)
        : item?.path
    } else {
      basePath = item?.path
    }

    // Determine if this route needs a prefix based on new routing structure
    let finalHref = basePath

    // Check if path needs site or corporate prefix
    const isSiteRoute = SITE_LEVEL_ROUTES.some((route) => basePath.includes(route))
    const isCorporateRoute = CORPORATE_LEVEL_ROUTES.some((route) => basePath.includes(route))

    // Only add prefix if path doesn't already have it
    if (!basePath.startsWith('/s/') && !basePath.startsWith('/c/')) {
      if (isSiteRoute && siteId) {
        finalHref = `/s/${siteId}/${basePath}`
      } else if (isCorporateRoute) {
        finalHref = `/c/${basePath}`
      } else if (basePath === 'profile') {
        // Profile is accessible from both contexts - use current context
        finalHref = siteId ? `/s/${siteId}/${basePath}` : `/c/${basePath}`
      } else if (basePath === 'settings' && siteId) {
        // Settings root can be site or corporate, default to site if we have siteId
        finalHref = `/s/${siteId}/${basePath}`
      } else if (basePath === 'settings' && !siteId) {
        finalHref = `/c/${basePath}`
      } else if (basePath === '/' || basePath === '') {
        // Root path - keep as is if no siteId, otherwise add site prefix
        finalHref = siteId ? `/s/${siteId}` : '/'
      }
    }

    return {
      label:
        hasParamsInRoute && hasRequiredParams && routeParamKey && hasSpecificRequiredParam
          ? dynamicBreadCrumbObject[routeParamKey]
          : item?.title,
      href: finalHref,
    }
  })
  return breadcrumbs
}
